import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { SubmissionService } from '../../submission-data/submission.service';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { generalConfigFromGlobal } from '../../../shared/workspace-element-context';
import {
  EfileGridCallbackEvent,
  EfileGridConfig,
  EfileGridDirective,
} from '../../shared/efile-grid.directive';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  CtrlFnPlan,
  EfileCtrlFnDispatcherService,
  EfileCtrlFnEvent,
  SearchReplaceContext,
} from '../scrubbing-actions/efile-ctrlfn-dispatcher.service';
import { EfileScrubbingActionsService } from '../scrubbing-actions/efile-scrubbing-actions.service';

/**
 * Legacy ui-router state names we expose as tabs.
 * Source: `ui/app/components/efile/efile.js` L228/L382/L508.
 */
type ScrubbingTabKey = 'list' | 'run' | 'history';

interface ScrubbingTab {
  key: ScrubbingTabKey;
  label: string;
  /** Reference to the legacy ui-router state this tab replaces. */
  legacyStateUrl: string;
  /** Base grid config (no submission_id yet — injected at render time). */
  gridConfigBase: Omit<EfileGridConfig, 'extraParams'> & {
    extraParams?: Record<string, any>;
  };
}

/**
 * Angular 12 port of:
 *   - `ui/app/components/efile/efileElements/efile-scrubbing.html`
 *   - `EfileScrubbingController` in `EfileElementsController.js` (L4811) — a
 *     pure tab shell.
 *
 * Each tab now mounts a real `[efileGrid]` using the exact
 * grid_id / action configuration from `efile.js`:
 *   list    -> action_code=9eu90y, grid_id=6074  (L228)
 *   run     -> action_id=33414,    grid_id=6172  (L382)
 *   history -> action_id=33418,    grid_id=6174  (L508)
 *
 * The legacy resolvers inject `submission_id` from
 * `EfileFactory.selectedSubmission`; in Angular 12 we read the same value
 * from {@link SubmissionService.activeSubmission}.
 */
@Component({
    selector: 'gtw-efile-scrubbing',
    templateUrl: './efile-scrubbing.component.html',
    styleUrls: ['./efile-scrubbing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileScrubbingComponent implements OnInit, OnChanges, OnDestroy {
  private readonly subs = new Subscription();

  /**
   * Pattern B deep-link key forwarded from `SubmissionDetailComponent`.
   * Matches the URL slug of legacy children
   * `submissions-dasboard.submission.efileScrubbing.<list|run|history>`
   * (efile.js L228/L382/L508). When set on the route
   * `/submission/:id/efile-scrubbing/list` this component pre-selects
   * the matching internal tab without disrupting users who land on the
   * parent URL (`/efile-scrubbing` only) and expect the default tab.
   */
  @Input() childSubState: string = '';

  readonly tabs: ScrubbingTab[] = [
    {
      key: 'list',
      label: 'Scrubbing list',
      legacyStateUrl: 'submissions-dasboard.submission.efileScrubbing.list',
      gridConfigBase: {
        actionCode: '9eu90y',
        gridId: '6074',
        exportActionId: 33070,
        legacyGridTplExport: true,
        exportScreenKey: 11212,
        exportDisplayDefaults: {
          display_mE_code: 'true',
          display_scenario_description: 'true',
          display_filing_group_description: '1',
        },
        autoLoad: true,
      },
    },
    {
      key: 'run',
      label: 'Run Scrubbing',
      legacyStateUrl: 'submissions-dasboard.submission.efileScrubbing.run',
      gridConfigBase: {
        actionId: 33414,
        gridId: '6172',
        emptyAlertFilterParams: ['form_key', 'search_key'],
        legacyGridTplExport: true,
        exportScreenKey: 11212,
        exportDisplayDefaults: {
          display_mE_code: 'true',
          display_scenario_description: 'true',
          display_filing_group_description: '1',
        },
        autoLoad: true,
      },
    },
    {
      key: 'history',
      label: 'Scrubbing History',
      legacyStateUrl: 'submissions-dasboard.submission.efileScrubbing.history',
      gridConfigBase: {
        actionId: 33418,
        gridId: '6174',
        emptyAlertFilterParams: ['search_key'],
        legacyGridTplExport: true,
        exportScreenKey: 11212,
        exportDisplayDefaults: {
          display_mE_code: 'true',
          display_scenario_description: 'true',
          display_filing_group_description: '1',
        },
        autoLoad: true,
      },
    },
  ];

  selectedKey: ScrubbingTabKey = 'list';

  /**
   * Live handle to every rendered `[efileGrid]` directive. The filter-bar
   * `<select>` elements live inside a sibling `*ngIf` block from the grid,
   * so a template ref var (`#gridRef`) isn't in scope there. Resolving the
   * active grid via `@ViewChildren` keeps filter dropdowns wired to the
   * correct directive regardless of which tab is mounted.
   */
  @ViewChildren(EfileGridDirective) private gridDirectives!: QueryList<EfileGridDirective>;

  /**
   * "Form By Submission" panel — limit document:click dismissal to outside
   * this wrapper only. The scrubbing host also contains the gizmo grid; using
   * the whole component for `contains()` left the menu open on grid clicks.
   */
  @ViewChild('formMultiselectWrapper', { read: ElementRef })
  private formMultiselectWrapper?: ElementRef<HTMLElement>;

  /** Cached grid configs keyed by tab. Built once on init so we pass the
   * same object reference each change-detection cycle (avoids re-creating
   * EfileGridDirective when the component flips between tabs). */
  private gridConfigByTab: Record<ScrubbingTabKey, EfileGridConfig | null> = {
    list: null,
    run: null,
    history: null,
  };

  // Confirmation-modal state (mirrors EfileElementsGridTplComponent).
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm Action',
    message: 'Are you sure you want to proceed?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orangeDark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingPlan: CtrlFnPlan | null = null;
  private pendingGrid: EfileGridDirective | null = null;

  // -------------------------------------------------------------------------
  // Filter state (run + history tabs).
  //
  // Mirror of legacy `efile.js` L401-453 / L527-555. Each tab declares its
  // own filter set; the loader runs once per tab when the user first
  // visits it (and the active submission is known).
  // -------------------------------------------------------------------------

  /**
   * Run tab — "Form By Submission" multi-select. Mirrors the admin
   * data-filter `gt-multiselect-wrapper` contract (selected is an array
   * of full row objects, not bare value strings).
   */
  formOptions: Array<{ FORM: string }> = [];
  selectedForms: Array<{ FORM: string }> = [];
  /** Internal UI-state for the multi-select panel (open flag + search text). */
  formFilterUi: { _menuOpen: boolean; _searchText: string } = {
    _menuOpen: false,
    _searchText: '',
  };

  /**
   * Run + History tab — "Scrubbing list" single-select. Stored as
   * a full row object so we keep all three columns the dropdown / API
   * need:
   *   - `LABEL`       – human-readable text shown in the `<option>`
   *                     (backend column literal; e.g. "nagative value").
   *   - `SEARCH_KEY`  – the actual filter token sent as `search_key`
   *                     on the grid request (backend `VALUE` column;
   *                     e.g. "-1000"). The legacy `df-select-single`
   *                     declared `value_name: "SEARCH_KEY"`, so we keep
   *                     that property name here.
   *   - `REPLACE_KEY` – used by `searchKeyToReplaceKey` ctrlFn.
   * Display vs value are intentionally split because the live backend
   * payload returns `{ LABEL, VALUE, REPLACE_KEY }` (e.g. LABEL
   * "nagative value" → VALUE "-1000"); collapsing them into one column
   * would either hide the human label or send the label as the
   * `search_key` param.
   */
  searchKeyOptions: Array<{
    SEARCH_KEY: string;
    REPLACE_KEY?: string;
    LABEL: string;
  }> = [];
  selectedSearchKeyRowByTab: Partial<
    Record<
      ScrubbingTabKey,
      { SEARCH_KEY: string; REPLACE_KEY?: string; LABEL: string } | null
    >
  > = {};

  loadingFilters: Record<ScrubbingTabKey, boolean> = {
    list: false,
    run: false,
    history: false,
  };

  // -------------------------------------------------------------------------
  // List-tab Add/Edit modal (mirror of efile.js L274-369).
  // -------------------------------------------------------------------------
  showAddEditModal = false;
  /** Row passed to <gtw-efile-create-new-scrubbing>. Null when adding. */
  addEditRowData: any = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private submissionService: SubmissionService,
    private appService: AppService,
    private globalService: GlobalService,
    private ctrlFnDispatcher: EfileCtrlFnDispatcherService,
    private scrubbingActions: EfileScrubbingActionsService,
  ) {}

  ngOnInit(): void {
    this.buildGridConfigs();
    this.applyChildSubState(this.childSubState);
    this.loadFilterOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['childSubState']) {
      this.applyChildSubState(this.childSubState);
      if (
        changes['childSubState'].firstChange === false &&
        (this.selectedKey === 'run' || this.selectedKey === 'history')
      ) {
        this.reloadScrubbingListLookup();
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Adopt the URL-driven child segment as the active tab when it matches
   * a known key. Unknown / empty values are ignored so the default
   * `selectedKey = 'list'` survives a plain `/efile-scrubbing` URL.
   */
  private applyChildSubState(raw: string): void {
    if (!raw) return;
    const normalized = raw.trim().toLowerCase() as ScrubbingTabKey;
    if (this.tabs.some((t) => t.key === normalized)) {
      this.selectedKey = normalized;
      this.cdr.markForCheck();
    }
  }

  // ---------------------------------------------------------------------------
  // gizmoGrid `(gridCallback)` bridge — routes `enableDisableScrubbing`
  // (and any future efile scrubbing ctrlFn callNames) through the shared
  // dispatcher service.
  // ---------------------------------------------------------------------------

  onGridCallback(event: EfileGridCallbackEvent, grid: EfileGridDirective): void {
    // List tab default branch — legacy efile.js L274 opens
    // create-new-scrubbing as a $uibModal for any callName other than
    // `enableDisableScrubbing`. Mirror that here so row grid actions
    // surface the Add/Update modal without adding a separate toolbar.
    if (this.selectedKey === 'list' && event.callName !== 'enableDisableScrubbing') {
      this.openAddEditModal(event);
      this.pendingGrid = grid;
      return;
    }

    // Run / history `searchKeyToReplaceKey` — enrich the event with the
    // current filter selections so the dispatcher has everything it
    // needs without reaching back into the host.
    let dispatchEvent: EfileCtrlFnEvent = event;
    if (event.callName === 'searchKeyToReplaceKey') {
      dispatchEvent = {
        ...event,
        searchReplaceContext: this.buildSearchReplaceContext(this.selectedKey),
      };
    }

    const plan = this.ctrlFnDispatcher.plan(dispatchEvent);
    if (!plan) return;

    if (!plan.confirmConfig) {
      this.executePlan(plan, grid);
      return;
    }

    this.pendingPlan = plan;
    this.pendingGrid = grid;
    this.confirmModalConfig = { ...this.confirmModalConfig, ...plan.confirmConfig };
    this.showConfirmModal = true;
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // List-tab Add/Edit modal (port of efile.js L274-369).
  // ---------------------------------------------------------------------------

  /**
   * Pull the first selected row out of the gridCallback payload (legacy
   * code passes the same `data` array), then open the inline modal.
   * Empty data → "Add" mode; one or more rows → "Edit" mode using the
   * first row as the seed (matches legacy `data[0].data`).
   */
  openAddEditModal(event: EfileGridCallbackEvent): void {
    const rows = Array.isArray(event.data) ? event.data : [];
    const firstRow = rows.length > 0 ? rows[0]?.data ?? rows[0] : null;
    this.addEditRowData = firstRow ? this.buildAddEditRowSeed(firstRow) : null;
    this.showAddEditModal = true;
    this.cdr.markForCheck();
  }

  /**
   * Translate a grid row into the shape `CreateNewScrubbingComponent.init`
   * expects (it reads LABEL/SEARCH_KEY/REPLACE_KEY/SCRUBBING_ID +
   * SUBMISSION_ID/TAX_YEAR for the save). We always fold submission
   * context in so the modal works even when the row payload omits it.
   */
  private buildAddEditRowSeed(row: any): any {
    const submission = this.submissionService.activeSubmission;
    return {
      ...row,
      SUBMISSION_ID: row?.SUBMISSION_ID ?? submission?.SUBMISSION_ID ?? '',
      TAX_YEAR: row?.TAX_YEAR ?? submission?.TAX_YEAR ?? '',
    };
  }

  closeAddEditModal(detail?: unknown): void {
    this.showAddEditModal = false;
    const shouldRefresh = this.isRefreshGridDetail(detail);
    const gridSnapshot = this.pendingGrid;
    this.pendingGrid = null;
    if (shouldRefresh) {
      // Defer until the overlay is gone so `activeGrid()` / gizmoGrid see
      // a stable host; also covers cases where `pendingGrid` was cleared
      // elsewhere but the list tab grid is still mounted.
      setTimeout(() => {
        (gridSnapshot ?? this.activeGrid())?.refresh();
        this.reloadScrubbingListLookup(true);
        this.cdr.markForCheck();
      }, 0);
    }
    this.cdr.markForCheck();
  }

  /** Accepts Angular @Output payload or DOM-like `{ detail: 'refresh' }`. */
  private isRefreshGridDetail(detail: unknown): boolean {
    if (detail === 'refresh') {
      return true;
    }
    if (detail && typeof detail === 'object' && 'detail' in (detail as object)) {
      return (detail as { detail?: unknown }).detail === 'refresh';
    }
    return false;
  }

  onConfirmModal(): void {
    const plan = this.pendingPlan;
    const grid = this.pendingGrid;
    this.showConfirmModal = false;
    this.pendingPlan = null;
    this.pendingGrid = null;
    this.cdr.markForCheck();
    if (plan && grid) {
      this.executePlan(plan, grid);
    }
  }

  onCancelModal(): void {
    this.showConfirmModal = false;
    this.pendingPlan = null;
    this.pendingGrid = null;
    this.cdr.markForCheck();
  }

  private executePlan(plan: CtrlFnPlan, grid: EfileGridDirective): void {
    this.subs.add(
      plan.execute().subscribe((result) => {
        if (result === 'refresh') {
          grid.refresh();
        }
      }),
    );
  }

  selectTab(key: ScrubbingTabKey): void {
    const sameTab = this.selectedKey === key;
    this.selectedKey = key;
    if (key === 'run' || key === 'history') {
      this.reloadScrubbingListLookup();
    }
    this.cdr.markForCheck();
    // Re-clicking the active tab does not remount the grid (*ngIf unchanged),
    // so force a server round-trip the same way the filter refresh icon does.
    if (sameTab) {
      setTimeout(() => {
        this.activeGrid()?.refresh();
        this.cdr.markForCheck();
      }, 0);
    }
  }

  isSelected(key: ScrubbingTabKey): boolean {
    return this.selectedKey === key;
  }

  get selectedTab(): ScrubbingTab | undefined {
    return this.tabs.find((t) => t.key === this.selectedKey);
  }

  gridConfigFor(key: ScrubbingTabKey): EfileGridConfig | null {
    return this.gridConfigByTab[key];
  }

  hasActiveSubmission(): boolean {
    return !!this.submissionService.activeSubmission?.SUBMISSION_ID;
  }

  private buildGridConfigs(): void {
    const submission = this.submissionService.activeSubmission;
    if (!submission?.SUBMISSION_ID) {
      // Leave configs null — template renders a "select a submission"
      // placeholder until the host resolves one.
      return;
    }

    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    const baseExtra: Record<string, any> = {
      submission_id: String(submission.SUBMISSION_ID),
    };

    const taxYearValues: any[] =
      (this.globalService as any).global?.inputs?.tax_year?.values || [];
    const taxYearEntry = taxYearValues.find(
      (v: any) => String(v.value) === String(submission.TAX_YEAR),
    );
    if (taxYearEntry?.scenario != null && String(taxYearEntry.scenario).trim() !== '') {
      baseExtra.scenario_id = String(taxYearEntry.scenario);
    }

    for (const tab of this.tabs) {
      this.gridConfigByTab[tab.key] = {
        ...tab.gridConfigBase,
        extraParams: { ...baseExtra, ...this.tabFilterParams(tab.key) },
        cacheKey: `efile/scrubbing/${tab.key}/${submission.SUBMISSION_ID}`,
        // Tab body is *ngIf-remounted on each visit; without this, cache
        // replays stale rows and the loadGridJson API never runs again.
        skipCacheOnInitialLoad: true,
      };
    }
  }

  // ---------------------------------------------------------------------------
  // Filter loading + selection (run + history tabs, port of efile.js
  // L401-453 / L527-555).
  // ---------------------------------------------------------------------------

  private loadFilterOptions(): void {
    const submission = this.submissionService.activeSubmission;
    if (!submission?.SUBMISSION_ID) return;

    const baseUrl = generalConfigFromGlobal(this.globalService).base_url || '/gtw';
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';

    // Run tab uses both lookups; history tab only needs the search-key
    // lookup. Both responses follow the standard `{ jsonObject: [...] }`
    // envelope used by the rest of the efile screens.
    this.loadingFilters = { ...this.loadingFilters, run: true, history: true };
    this.cdr.markForCheck();

    this.subs.add(
      this.scrubbingActions
        .loadFormsBySubmission(
          baseUrl,
          submission.SUBMISSION_ID,
          submission.TAX_YEAR,
        )
        .subscribe(
          (response: any) => {
            this.formOptions = this.normalizeFormOptions(response);
            this.cdr.markForCheck();
          },
          () => {
            this.formOptions = [];
            this.cdr.markForCheck();
          },
        ),
    );

    this.subs.add(
      this.scrubbingActions
        .loadScrubbingListLookup(baseUrl, submission.SUBMISSION_ID)
        .subscribe(
          (response: any) => {
            this.applyScrubbingListLookupResponse(response);
          },
          () => {
            this.applyScrubbingListLookupError();
          },
        ),
    );
  }

  /**
   * Re-fetch "Scrubbing list" dropdown options (run + history tabs) from the
   * server whenever those tabs are selected or after list mutations.
   */
  private reloadScrubbingListLookup(fresh = false): void {
    const submission = this.submissionService.activeSubmission;
    if (!submission?.SUBMISSION_ID) {
      return;
    }
    const baseUrl = generalConfigFromGlobal(this.globalService).base_url || '/gtw';
    this.loadingFilters = { ...this.loadingFilters, run: true, history: true };
    this.cdr.markForCheck();
    this.subs.add(
      this.scrubbingActions
        .loadScrubbingListLookup(baseUrl, submission.SUBMISSION_ID, fresh)
        .subscribe(
          (response: any) => {
            this.applyScrubbingListLookupResponse(response);
          },
          () => {
            this.applyScrubbingListLookupError();
          },
        ),
    );
  }

  private applyScrubbingListLookupResponse(response: any): void {
    const next = this.normalizeSearchKeyOptions(response);
    this.searchKeyOptions = next;
    this.pruneStaleSearchKeySelections(next);
    this.loadingFilters = {
      ...this.loadingFilters,
      run: false,
      history: false,
    };
    this.cdr.markForCheck();
  }

  private applyScrubbingListLookupError(): void {
    this.searchKeyOptions = [];
    this.loadingFilters = {
      ...this.loadingFilters,
      run: false,
      history: false,
    };
    this.cdr.markForCheck();
  }

  /**
   * After a fresh lookup, drop run/history filter selections that no longer
   * exist in the option list (avoids sending a stale `search_key`).
   */
  private pruneStaleSearchKeySelections(
    options: Array<{ SEARCH_KEY: string; REPLACE_KEY?: string; LABEL: string }>,
  ): void {
    const labels = new Set(options.map((o) => o.LABEL));
    const keys = new Set(options.map((o) => o.SEARCH_KEY));
    let next = { ...this.selectedSearchKeyRowByTab };
    let changed = false;
    (['run', 'history'] as const).forEach((tab) => {
      const row = next[tab];
      if (!row) {
        return;
      }
      const ok = labels.has(row.LABEL) || keys.has(row.SEARCH_KEY);
      if (!ok) {
        next = { ...next, [tab]: null };
        changed = true;
      }
    });
    if (changed) {
      this.selectedSearchKeyRowByTab = next;
    }
  }

  /** Defensive unwrap — covers `{ jsonObject: [] }`, `{ data: [] }`, raw arrays. */
  private unwrapList(response: any, requiredFields: string[]): any[] {
    const list =
      (Array.isArray(response) && response) ||
      (Array.isArray(response?.jsonObject) && response.jsonObject) ||
      (Array.isArray(response?.data) && response.data) ||
      [];
    return list.filter((item: any) =>
      requiredFields.every((f) => item?.[f] !== undefined && item?.[f] !== null && item?.[f] !== ''),
    );
  }

  /**
   * Normalize "Form By Submission" lookup rows.
   * API contracts vary between legacy raw shape (`FORM`) and generic
   * filter shape (`LABEL`/`VALUE`), so we map both into `{ FORM }`.
   */
  private normalizeFormOptions(response: any): Array<{ FORM: string }> {
    const list =
      (Array.isArray(response) && response) ||
      (Array.isArray(response?.jsonObject) && response.jsonObject) ||
      (Array.isArray(response?.data) && response.data) ||
      [];
    return list
      .map((row: any) => {
        const form = String(
          row?.FORM ??
          row?.form ??
          row?.VALUE ??
          row?.value ??
          row?.LABEL ??
          row?.label ??
          '',
        ).trim();
        return { FORM: form };
      })
      .filter((row: { FORM: string }) => !!row.FORM);
  }

  /**
   * Normalize "Scrubbing list" lookup rows.
   *
   * The live backend response (`action_code=sjrs2c`) looks like:
   *
   *   { IS_ACTIVE: 1,
   *     LABEL: "nagative value",   // ← human-readable display text
   *     VALUE: "-1000",             // ← actual filter token
   *     REPLACE_KEY: "1000",
   *     SUBMISSION_ID: 4126013 }
   *
   * Older payloads used `SEARCH_KEY` as the filter token directly, so we
   * still accept that as a fallback. The normalized shape preserves
   * `LABEL` separately so the dropdown can show the human name while
   * still sending the right `search_key` to the grid endpoint.
   */
  private normalizeSearchKeyOptions(
    response: any,
  ): Array<{ SEARCH_KEY: string; REPLACE_KEY?: string; LABEL: string }> {
    const list =
      (Array.isArray(response) && response) ||
      (Array.isArray(response?.jsonObject) && response.jsonObject) ||
      (Array.isArray(response?.data) && response.data) ||
      [];
    return list
      .map((row: any) => {
        // Filter token sent as `search_key` to the grid SP. Prefer
        // `VALUE` (current backend) over `SEARCH_KEY` (legacy column
        // name); fall back to `LABEL` only as a last resort so we
        // never end up with an empty value attribute.
        const searchKey = String(
          row?.VALUE ??
          row?.value ??
          row?.SEARCH_KEY ??
          row?.search_key ??
          row?.LABEL ??
          row?.label ??
          '',
        ).trim();
        // Display text. Prefer the dedicated `LABEL` column; if the
        // backend omits it (older shape) fall back to the filter token
        // itself so the option is at least visible to the user.
        const label = String(
          row?.LABEL ??
          row?.label ??
          searchKey,
        ).trim();
        const replaceKey = String(
          row?.REPLACE_KEY ??
          row?.replace_key ??
          row?.REPLACE_WITH ??
          row?.replace_with ??
          '',
        ).trim();
        return {
          SEARCH_KEY: searchKey,
          REPLACE_KEY: replaceKey || undefined,
          LABEL: label || searchKey,
        };
      })
      .filter((row: { SEARCH_KEY: string }) => !!row.SEARCH_KEY);
  }

  /**
   * Map of extra params to fold into the gridConfig for the given tab.
   * Mirrors how legacy gizmoGrid forwarded selected filters as request
   * params. Empty values are dropped so the SP defaults still apply.
   */
  private tabFilterParams(tab: ScrubbingTabKey): Record<string, any> {
    const out: Record<string, any> = {};
    if (tab === 'run' && this.selectedForms.length > 0) {
      out['form_key'] = this.selectedForms.map((f) => f.FORM).join(',');
    }
    const searchKeyRow = this.selectedSearchKeyRowByTab[tab];
    if ((tab === 'run' || tab === 'history') && searchKeyRow?.SEARCH_KEY) {
      out['search_key'] = searchKeyRow.SEARCH_KEY;
    }
    return out;
  }

  /**
   * Build the snapshot the dispatcher needs for `searchKeyToReplaceKey`.
   * Run tab — search/replace come from the selected scrubbing entry
   * directly (legacy `filters[1].selected.map(... VALUE)` /
   * `... REPLACE_KEY`). History tab — legacy swaps them
   * (`search_key: filters[0].selected.map(... REPLACE_KEY)` etc.) so
   * we mirror that flip below.
   */
  private buildSearchReplaceContext(tab: ScrubbingTabKey): SearchReplaceContext {
    const selectedRow = this.selectedSearchKeyRowByTab[tab] || null;
    const selectedSearchKey = selectedRow?.SEARCH_KEY ?? '';
    const replaceKey = selectedRow?.REPLACE_KEY ?? '';
    if (tab === 'history') {
      return {
        searchKeys: replaceKey,
        replaceKeys: selectedSearchKey,
      };
    }
    return {
      searchKeys: selectedSearchKey,
      replaceKeys: replaceKey,
      formKeys: this.selectedForms.map((f) => f.FORM).join(','),
    };
  }

  /**
   * Re-apply the filter selections to the affected tab's gridConfig and
   * trigger a refresh. Called by the template's filter-bar dropdowns.
   */
  applyFilters(tab: ScrubbingTabKey, grid: EfileGridDirective | undefined): void {
    const cfg = this.gridConfigByTab[tab];
    if (!cfg) return;
    const submission = this.submissionService.activeSubmission;
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    this.gridConfigByTab[tab] = {
      ...cfg,
      extraParams: {
        submission_id: submission?.SUBMISSION_ID,
        sso_id: ssoId,
        ...this.tabFilterParams(tab),
      },
    };
    this.cdr.markForCheck();
    if (grid) {
      // The new gridConfig reaches the directive on the next change-detection
      // pass, so hand the selections to this refresh directly.
      grid.refresh(this.tabFilterParams(tab));
    }
  }

  /**
   * Apply the active tab's filter selections — exposed to the green
   * `Filter` button (mirror of admin `data-filters` `refresh()`). Pulls
   * the active grid out of the QueryList so we don't depend on a
   * template ref that lives in a sibling `*ngIf`.
   */
  applyActiveTabFilters(): void {
    this.applyFilters(this.selectedKey, this.activeGrid());
  }

  // ---------------------------------------------------------------------------
  // Multi-select panel helpers (mirror admin DataFiltersComponent —
  // `gt-multiselect-wrapper` UX). Keeps the visual contract identical
  // to the submission-list / form-by-submission filter dropdowns.
  // ---------------------------------------------------------------------------

  toggleFormMenu(): void {
    this.formFilterUi = {
      ...this.formFilterUi,
      _menuOpen: !this.formFilterUi._menuOpen,
    };
    this.cdr.markForCheck();
  }

  isFormChecked(opt: { FORM: string }): boolean {
    return this.selectedForms.some((s) => s.FORM === opt.FORM);
  }

  toggleFormItem(opt: { FORM: string }): void {
    const idx = this.selectedForms.findIndex((s) => s.FORM === opt.FORM);
    if (idx >= 0) {
      const next = [...this.selectedForms];
      next.splice(idx, 1);
      this.selectedForms = next;
    } else {
      this.selectedForms = [...this.selectedForms, opt];
    }
    this.cdr.markForCheck();
  }

  selectAllForms(): void {
    this.selectedForms = [...this.formOptions];
    this.cdr.markForCheck();
  }

  unselectAllForms(): void {
    this.selectedForms = [];
    this.cdr.markForCheck();
  }

  getFilteredFormOptions(): Array<{ FORM: string }> {
    const search = (this.formFilterUi._searchText || '').toLowerCase();
    if (!search) return this.formOptions.slice(0, 200);
    return this.formOptions
      .filter((v) => (v.FORM || '').toLowerCase().includes(search))
      .slice(0, 200);
  }

  /**
   * Native `<select>` change handler for the Scrubbing single-select.
   *
   * The `<option>` `value` attribute is bound to `opt.LABEL` (so the
   * dropdown shows / posts the human label as its identity), but the
   * row we cache on `selectedSearchKeyRowByTab` keeps the full
   * normalized record so downstream consumers can still read
   * `SEARCH_KEY` (the backend `VALUE` token used for the grid's
   * `search_key` filter param) and `REPLACE_KEY` (used by
   * `searchKeyToReplaceKey`). Matching here therefore has to use
   * `LABEL` rather than `SEARCH_KEY`.
   */
  onSearchKeyValueChange(tab: ScrubbingTabKey, value: string): void {
    const matched =
      this.searchKeyOptions.find((o) => o.LABEL === value) ?? null;
    this.selectedSearchKeyRowByTab = {
      ...this.selectedSearchKeyRowByTab,
      [tab]: matched,
    };
    this.cdr.markForCheck();
  }

  /**
   * Close the multi-select when the click is outside the multiselect
   * wrapper only. The scrubbing component's root also wraps the grid; using
   * `host.contains()` treated grid clicks as "inside" and never closed
   * the panel (unlike admin data-filters, where the filter bar is the main
   * host content).
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.formFilterUi._menuOpen) return;
    const wrap = this.formMultiselectWrapper?.nativeElement;
    const target = event.target as Node | null;
    if (wrap && target && wrap.contains(target)) {
      return;
    }
    this.formFilterUi = { ...this.formFilterUi, _menuOpen: false };
    this.cdr.markForCheck();
  }

  /**
   * Returns the `[efileGrid]` directive currently mounted under the active
   * tab. Only one tab body is rendered at a time (the wrapper div uses
   * `*ngIf="isTabActive(tab.key)"`), so the QueryList typically holds zero
   * or one directive.
   */
  private activeGrid(): EfileGridDirective | undefined {
    return this.gridDirectives?.first;
  }
}
